import {
  AVAX,
  BASE,
  BCH,
  BSC,
  BTC,
  DOGE,
  ETH,
  GAIA,
  LTC,
  Network,
  SOL,
  THOR,
  TRON,
  XRP,
  gasToken,
} from "rujira.js";

// Selling to Banxa means sending the asset on-chain afterwards, which costs gas.
// Offering the full balance as the maximum creates an order the user cannot
// fund, and it stalls at the transfer step instead of failing up front.
//
// Amounts are in the app's 8-decimal representation and cover a single outbound
// transfer with headroom. They are deliberately conservative rather than exact:
// revisit them with real fee data if Banxa reports funding failures.
const GAS_RESERVE: Partial<Record<Network, bigint>> = {
  [AVAX]: 5_000_000n, // 0.05 AVAX
  [BASE]: 50_000n, // 0.0005 ETH
  [BCH]: 100_000n, // 0.001 BCH
  [BSC]: 300_000n, // 0.003 BNB
  [BTC]: 30_000n, // 0.0003 BTC
  [DOGE]: 500_000_000n, // 5 DOGE
  [ETH]: 500_000n, // 0.005 ETH
  [GAIA]: 2_000_000n, // 0.02 ATOM
  [LTC]: 100_000n, // 0.001 LTC
  [SOL]: 1_000_000n, // 0.01 SOL
  [THOR]: 5_000_000n, // 0.05 RUNE
  [TRON]: 3_000_000_000n, // 30 TRX
  // The ledger keeps 1 XRP as the base reserve, so the fee comes on top of it.
  [XRP]: 110_000_000n, // 1.1 XRP
};

const isGasToken = (network: Network, symbol: string): boolean =>
  symbol.trim().toUpperCase() === gasToken(network).symbol.toUpperCase();

export const banxaGasReserve = (network: Network, symbol: string): bigint =>
  isGasToken(network, symbol) ? GAS_RESERVE[network] || 0n : 0n;

/**
 * True when `symbol` is not the network's gas token and the wallet holds less gas
 * than one outbound transfer needs. Selling it would create an order the user
 * cannot fund.
 */
export const banxaNeedsGas = (
  network: Network,
  symbol: string,
  gasBalance: bigint
): boolean =>
  !isGasToken(network, symbol) && gasBalance < (GAS_RESERVE[network] || 0n);

/**
 * The largest amount of `symbol` on `network` that can be sold and still leave
 * enough of the network's gas token to send it. Non-gas assets sell in full, as
 * long as the wallet holds gas to send them (`gasBalance`).
 */
export const banxaSellMax = (
  network: Network,
  symbol: string,
  balance: bigint,
  gasBalance?: bigint
): bigint => {
  if (gasBalance !== undefined && banxaNeedsGas(network, symbol, gasBalance))
    return 0n;
  const reserve = banxaGasReserve(network, symbol);
  return balance > reserve ? balance - reserve : 0n;
};
